import { useEffect } from 'react';

declare global {
  interface Window {
    atOptions: any;
  }
}

// Ad configuration
const AD_CONFIG = {
  key: process.env.REACT_APP_ADSTERRA_PUBLISHER_ID,
  format: 'iframe',
  height: 300,
  width: 160,
  params: {}
};

// Track last ad show time to maintain frequency
let lastAdShowTime = 0;
const MIN_AD_INTERVAL = 30000; // 30 seconds between ads

// Track navigation count
let navigationCount = 0;
const NAVIGATION_AD_THRESHOLD = 2; // Show ad every 2 navigations

// Error boundary for ad scripts
const handleAdError = (error: any) => {
  console.warn('Ad script error:', error);
  return true; // Prevent error from propagating
};

const loadScript = (src: string, attributes: Record<string, string> = {}) => {
  return new Promise<void>((resolve) => {
    try {
      // Check if script already exists
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      
      // Add any additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });

      // Handle script loading
      script.onload = () => {
        console.log(`Script loaded successfully: ${src}`);
        resolve();
      };
      
      script.onerror = () => {
        console.warn(`Script failed to load: ${src}`);
        resolve(); // Resolve even on error
      };

      // Add script directly to document head
      document.head.appendChild(script);

    } catch (error) {
      console.warn('Error in script loading:', error);
      resolve(); // Resolve even on error
    }
  });
};

// Function to check if we should show an ad
const shouldShowAd = () => {
  const now = Date.now();
  if (now - lastAdShowTime >= MIN_AD_INTERVAL) {
    lastAdShowTime = now;
    return true;
  }
  return false;
};

// Ad service functions
export const adService = {
  // Direct Link Ad
  loadDirectLinkAd: () => {
    try {
      if (!shouldShowAd()) return;
      window.location.href = 'https://www.effectiveratecpm.com/kygiwwz2y?key=fb70cd46648c9121ff6523441ac5e943';
    } catch (error) {
      console.error('Error loading direct link ad:', error);
    }
  },

  // Timer with ads
  startAdTimer: async (duration: number = 10, onComplete: () => void) => {
    return new Promise<void>((resolve) => {
      try {
        let timeLeft = duration;
        const timerElement = document.createElement('div');
        timerElement.style.position = 'fixed';
        timerElement.style.top = '20px';
        timerElement.style.right = '20px';
        timerElement.style.backgroundColor = 'rgba(0,0,0,0.8)';
        timerElement.style.color = 'white';
        timerElement.style.padding = '10px 20px';
        timerElement.style.borderRadius = '5px';
        timerElement.style.zIndex = '9999';
        timerElement.textContent = `Please wait ${timeLeft} seconds`;
        document.body.appendChild(timerElement);

        const timer = setInterval(() => {
          timeLeft--;
          timerElement.textContent = `Please wait ${timeLeft} seconds`;
          
          if (timeLeft <= 0) {
            clearInterval(timer);
            document.body.removeChild(timerElement);
            onComplete();
            resolve();
          }
        }, 1000);
      } catch (error) {
        console.error('Error in ad timer:', error);
        resolve();
      }
    });
  },

  // Load banner ad
  loadBannerAd: async (containerId: string) => {
    try {
      if (!shouldShowAd()) return;

      const container = document.getElementById(containerId);
      if (!container) {
        console.warn(`Container ${containerId} not found`);
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.text = `atOptions = ${JSON.stringify(AD_CONFIG)};`;
      document.head.appendChild(script);

      const adScript = document.createElement('script');
      adScript.type = 'text/javascript';
      adScript.src = `//www.highperformanceformat.com/${AD_CONFIG.key}/invoke.js`;
      adScript.async = true;
      adScript.onerror = () => console.warn('Banner ad script failed to load');
      document.head.appendChild(adScript);
    } catch (error) {
      console.error('Error loading banner ad:', error);
    }
  },

  // Load popunder ad
  loadPopunderAd: async () => {
    try {
      if (!shouldShowAd()) return;

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.text = `atOptions = ${JSON.stringify({
        ...AD_CONFIG,
        format: 'popunder'
      })};`;
      document.head.appendChild(script);

      const adScript = document.createElement('script');
      adScript.type = 'text/javascript';
      adScript.src = `//www.highperformanceformat.com/${AD_CONFIG.key}/invoke.js`;
      adScript.async = true;
      adScript.onerror = () => console.warn('Popunder ad script failed to load');
      document.head.appendChild(adScript);
    } catch (error) {
      console.error('Error loading popunder ad:', error);
    }
  },

  // Load interstitial ad
  loadInterstitialAd: async () => {
    try {
      if (!shouldShowAd()) return;

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.text = `atOptions = ${JSON.stringify({
        ...AD_CONFIG,
        format: 'interstitial'
      })};`;
      document.head.appendChild(script);

      const adScript = document.createElement('script');
      adScript.type = 'text/javascript';
      adScript.src = `//www.highperformanceformat.com/${AD_CONFIG.key}/invoke.js`;
      adScript.async = true;
      adScript.onerror = () => console.warn('Interstitial ad script failed to load');
      document.head.appendChild(adScript);
    } catch (error) {
      console.error('Error loading interstitial ad:', error);
    }
  },

  // Enhanced multi-step ads function
  showMultiStepAds: async (callback: () => void) => {
    if (!shouldShowAd()) {
      callback();
      return;
    }

    try {
      // Show popunder
      (window as any).triggerPopunder?.();
      
      // Show social bar
      (window as any).triggerSocialBar?.();

      // Delay callback to ensure ads have time to load
      setTimeout(() => {
        callback();
      }, 1000);
    } catch (error) {
      console.error('Error showing ads:', error);
      callback();
    }
  },

  // Function to handle navigation ads
  handleNavigationAd: () => {
    navigationCount++;
    if (navigationCount >= NAVIGATION_AD_THRESHOLD) {
      navigationCount = 0;
      if (shouldShowAd()) {
        (window as any).triggerPopunder?.();
      }
    }
  }
};

// Hook for banner ads
export const useBannerAd = (containerId: string) => {
  useEffect(() => {
    const loadAd = () => {
      try {
        const container = document.getElementById(containerId);
        if (container && shouldShowAd()) {
          // Refresh the container to trigger new ad
          container.innerHTML = '';
          const script = document.createElement('script');
          script.async = true;
          script.src = '//pl26197492.effectiveratecpm.com/8d6f163ab876c15b559a8542693a18d6/invoke.js';
          container.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading banner ad:', error);
      }
    };

    loadAd();
    // Refresh ad every 60 seconds
    const interval = setInterval(loadAd, 60000);

    return () => clearInterval(interval);
  }, [containerId]);
};

// Hook for social bar ads
export const useSocialBarAd = () => {
  useEffect(() => {
    const loadAd = async () => {
      try {
        if (shouldShowAd()) {
          const script = document.createElement('script');
          script.src = '//pl26195712.effectiveratecpm.com/02/78/42/027842c1ea338c64ae6d6bb5a82cfb6a.js';
          document.head.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading social bar ad:', error);
      }
    };

    loadAd();
  }, []);
};

// Hook for native banner ads
export const useNativeBannerAd = (containerId: string) => {
  useEffect(() => {
    const loadAd = () => {
      try {
        const container = document.getElementById(containerId);
        if (container && shouldShowAd()) {
          container.innerHTML = '';
          const script = document.createElement('script');
          script.async = true;
          script.src = '//pl26197581.effectiveratecpm.com/78/3a/09/783a095c076a071bc2856ac9841c0305.js';
          container.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading native banner ad:', error);
      }
    };

    loadAd();
    // Refresh native banner every 45 seconds
    const interval = setInterval(loadAd, 45000);

    return () => clearInterval(interval);
  }, [containerId]);
};

// Hook for bottom native banners
export const useBottomNativeBanners = () => {
  useEffect(() => {
    const loadAds = () => {
      try {
        if (shouldShowAd()) {
          ['native-banner-bottom-1', 'native-banner-bottom-2'].forEach((id) => {
            const container = document.getElementById(id);
            if (container) {
              container.innerHTML = '';
              const script = document.createElement('script');
              script.async = true;
              script.src = '//pl26197581.effectiveratecpm.com/78/3a/09/783a095c076a071bc2856ac9841c0305.js';
              container.appendChild(script);
            }
          });
        }
      } catch (error) {
        console.error('Error loading bottom native banners:', error);
      }
    };

    loadAds();
    // Refresh bottom banners every 50 seconds
    const interval = setInterval(loadAds, 50000);

    return () => clearInterval(interval);
  }, []);
}; 